import React from 'react'
import { useTranslation } from 'react-i18next'
import { Icon } from 'components/Icons'
import styles from './Input.module.scss'

/**
 * Универсальное поле ввода с label, error, hint, icon, password-toggle.
 *
 *   <UnvInput label="Email" type="email" value={x} onChange={e=>setX(e.target.value)} />
 *   <UnvInput label="Пароль" type="password" required error={err} hint="Минимум 8 знаков" />
 *   <UnvInput iconLeft="search" placeholder="Поиск..." />
 */
const UnvInput = React.forwardRef(({
  label,
  hint,
  error,
  iconLeft,
  iconRight,
  type = 'text',
  required = false,
  fullWidth = true,
  size = 'md',
  className = '',
  inputClassName = '',
  id,
  ...rest
}, ref) => {
  const { t } = useTranslation('common')
  const [show, setShow] = React.useState(false)
  const isPwd = type === 'password'
  const realType = isPwd && show ? 'text' : type
  const autoId = React.useId()
  const inputId = id || `unv-input-${autoId}`
  const hintId = inputId ? `${inputId}-hint` : undefined
  const errorId = inputId ? `${inputId}-error` : undefined

  return (
    <div className={[
      styles.field,
      fullWidth ? styles.fullWidth : '',
      error ? styles.hasError : '',
      styles[`s_${size}`],
      className,
    ].filter(Boolean).join(' ')}>
      {label && (
        <label htmlFor={inputId} className={styles.label}>
          {label}
          {required && <span className={styles.requiredMark} aria-hidden="true">*</span>}
        </label>
      )}
      <div className={styles.inputWrap}>
        {iconLeft && <Icon name={iconLeft} size={16} color="var(--color-text-muted)" className={styles.iconLeft} />}
        <input
          ref={ref}
          id={inputId}
          type={realType}
          className={`${styles.input} ${inputClassName}`}
          required={required}
          aria-invalid={error ? 'true' : undefined}
          aria-describedby={[hint ? hintId : null, error ? errorId : null].filter(Boolean).join(' ') || undefined}
          {...rest}
        />
        {isPwd ? (
          <button
            type="button"
            className={styles.iconRightBtn}
            onClick={() => setShow((v) => !v)}
            aria-label={show ? t('auto.unvui_input.hide_password', 'Скрыть пароль') : t('auto.unvui_input.show_password', 'Показать пароль')}
            tabIndex={-1}
          >
            <Icon name={show ? 'eye-off' : 'eye'} size={16} color="var(--color-text-muted)" />
          </button>
        ) : iconRight ? (
          <Icon name={iconRight} size={16} color="var(--color-text-muted)" className={styles.iconRight} />
        ) : null}
      </div>
      {error ? (
        <span id={errorId} className={styles.error} role="alert">{error}</span>
      ) : hint ? (
        <span id={hintId} className={styles.hint}>{hint}</span>
      ) : null}
    </div>
  )
})

UnvInput.displayName = 'UnvInput'

export default UnvInput
